import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { REFRESH_SOURCE_FAILURE } from '../../model/constants.tsx';
import type { CardsFlagWithCommentResponse } from '../../rest/cards-flag-with-comment/types.tsx';
import cardsFlagWithCommentService from '../../services/cards-flag-with-comment/index.tsx';
import { refreshDataRequest } from '../../state/actions/app/index.tsx';
import {
	CARDS_FLAG_WITH_COMMENT_REQUEST,
	type CardsFlagWithCommentRequestAction,
	cardsFlagWithCommentSuccess,
	cardsFlagWithCommentFailure,
} from '../../state/actions/cards/flag-with-comment/index.tsx';
import type { ActionsObservable, MiddlewareAPI } from '../../state/types.tsx';

export const cardsFlagWithCommentEpic = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$
		.ofType(CARDS_FLAG_WITH_COMMENT_REQUEST)
		.mergeMap((action: CardsFlagWithCommentRequestAction) => {
			const {
				payload: { cards, flag },
				meta: {
					optimistic: { id: optimisticId },
					analyticsEvent,
				},
			} = action;
			return cardsFlagWithCommentService(store, action)
				.flatMap((response: CardsFlagWithCommentResponse) => {
					if (response === null) {
						// if we get here, all of the issues were successfully updated
						fireTrackAnalytics(analyticsEvent, 'cards flagged', {
							numCards: cards.length,
							flagAction: flag ? 'added' : 'removed',
						});
						return Observable.of(
							cardsFlagWithCommentSuccess(optimisticId, {
								cards,
							}),
						);
					}
					// if we get here, at least one of the issues failed to be updated,
					// so we treat it as a failure
					const error = new Error(response.errorMessages.join(', '));
					log.safeErrorWithoutCustomerData(
						'backlog.cards-flag-with-comment.partial-failure',
						'Failed to flag and/or comment on some issues.',
						error,
					);
					throw error;
				})
				.catch(() => {
					// if we get here directly from the cardsFlagWithCommentService, none
					// of the issues were successfully updated
					fireOperationalAnalytics(analyticsEvent, 'ui taskFail', {
						task: 'flagCards',
						numCards: cards.length,
						flagAction: flag ? 'added' : 'removed',
					});
					return Observable.from([
						cardsFlagWithCommentFailure(optimisticId, {
							cards,
						}),
						refreshDataRequest(REFRESH_SOURCE_FAILURE),
					]);
				});
		});
